import {
    ApiBaseService,
    type ApiServiceConfig,
    type PaginatedFetch,
} from "~/services/ApiBaseService";
import type { ApiResponse } from "~/types/ApiResponse";

export interface SalesForceField {
    aggregatable: boolean;
    aiPredictionField: boolean;
    autoNumber: boolean;
    byteLength: number;
    calculated: boolean;
    calculatedFormula: null;
    cascadeDelete: boolean;
    caseSensitive: boolean;
    compoundFieldName: null;
    controllerName: null;
    createable: boolean;
    custom: boolean;
    defaultValue: null;
    defaultValueFormula: null;
    defaultedOnCreate: boolean;
    dependentPicklist: boolean;
    deprecatedAndHidden: boolean;
    digits: number;
    displayLocationInDecimal: boolean;
    encrypted: boolean;
    externalId: boolean;
    extraTypeInfo: null;
    filterable: boolean;
    filteredLookupInfo: null;
    formulaTreatNullNumberAsZero: boolean;
    groupable: boolean;
    highScaleNumber: boolean;
    htmlFormatted: boolean;
    idLookup: boolean;
    inlineHelpText: null;
    label: string;
    length: number;
    mask: null;
    maskType: null;
    name: string;
    nameField: boolean;
    namePointing: boolean;
    nillable: boolean;
    permissionable: boolean;
    picklistValues: {
        active: boolean;
        defaultValue: boolean;
        label: string;
        validFor: null;
        value: string;
    }[];
    polymorphicForeignKey: boolean;
    precision: 0;
    queryByDistance: boolean;
    referenceTargetField: null;
    referenceTo: [];
    relationshipName: null;
    relationshipOrder: null;
    restrictedDelete: boolean;
    restrictedPicklist: boolean;
    scale: number;
    searchPrefilterable: boolean;
    soapType: string;
    sortable: boolean;
    type: string;
    unique: boolean;
    updateable: boolean;
    writeRequiresMasterRead: boolean;
}

export default class SalesForceService extends ApiBaseService {
    protected uuid: string;

    constructor(uuid: string, config: ApiServiceConfig) {
        super(config);

        this.uuid = uuid;
    }

    public describe(
        sobject: string,
        options: Partial<{
            tooling: boolean;
        }> = {},
    ): Promise<
        ApiResponse<{
            fields: SalesForceField[];
        }>
    > {
        return this.request(
            this.route(
                "api.connections.salesforce.describe",
                "/connections/salesforce/{connection}/{sobject}",
                {
                    connection: this.uuid,
                    sobject,
                },
            ),
            {
                method: "GET",
                params: options,
            },
        );
    }

    public updatableObjects(): Promise<
        ApiResponse<
            {
                label: string;
                value: string;
            }[]
        >
    > {
        return this.request(
            this.route(
                "api.connections.salesforce.updatableObjects",
                "/connections/salesforce/{connection}/updatable_objects",
                {
                    connection: this.uuid,
                },
            ),
            {
                method: "GET",
            },
        );
    }
    public getObjectWithRelations(sobject: string): Promise<
        ApiResponse<
            {
                label: string;
                value: string;
            }[]
        >
    > {
        return this.request(
            this.route(
                "api.connections.salesforce.object-with-relations",
                "/connections/salesforce/{connection}/object-with-relations/{sobject}",
                {
                    connection: this.uuid,
                    sobject,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public createableObjects(): Promise<
        ApiResponse<
            {
                label: string;
                value: string;
            }[]
        >
    > {
        return this.request(
            this.route(
                "api.connections.salesforce.createableObjects",
                "/connections/salesforce/{connection}/createable_objects",
                {
                    connection: this.uuid,
                },
            ),
            {
                method: "GET",
            },
        );
    }

    public field(
        sobject: string,
        field: string,
        options: Partial<{
            tooling: boolean;
        }> = {},
    ): Promise<ApiResponse<SalesForceField>> {
        return this.request(
            this.route(
                "api.connections.salesforce.describe.field",
                "/connections/salesforce/{connection}/{sobject}/{field}",
                {
                    connection: this.uuid,
                    sobject,
                    field,
                },
            ),
            {
                method: "GET",
                params: options,
            },
        );
    }

    public query(
        sobject: string,
        options: Partial<{
            columns: string[];
            transform: Record<string, string | false>;
            show: number;
            page: number;
        }> = {},
    ): PaginatedFetch<typeof options> {
        return this.paginate(
            this.route(
                "api.connections.salesforce.query",
                "/connections/salesforce/{connection}/{sobject}",
                {
                    connection: this.uuid,
                    sobject,
                },
            ),
            {
                fetch: {
                    method: "POST",
                    body: options,
                },
            } as unknown as any,
        );
    }
}
